import type { Edge, Node } from "reactflow"

import { BASE_URL } from "const/API"
import { TDataFilterParam } from "store/slice/AlgorithmNode/AlgorithmNodeType"
import type {
  AlgorithmNodeData,
  InputNodeData,
} from "store/slice/FlowElement/FlowElementType"
import type { FILE_TYPE } from "store/slice/InputNode/InputNodeType"
import axios from "utils/axios"
import type { ParamMap } from "utils/param/ParamType"

export type RunPostData = {
  name: string
  nodeDict: NodeDict
  edgeDict: EdgeDict
  nwbParam: ParamMap
  snakemakeParam: ParamMap
  forceRunList: { nodeId: string; name: string }[]
}

export type NodeDict = {
  [nodeId: string]: Node<NodePostDataType>
}

export type EdgeDict = {
  [nodeId: string]: Edge
}

export type NodePostDataType = AlgorithmNodePostData | InputNodePostData

export interface InputNodePostData extends InputNodeData {
  path: string | string[]
  fileType: FILE_TYPE
  param?: {
    [key: string]: unknown
  }
  dataFilterParam?: TDataFilterParam
  draftDataFilterParam?: TDataFilterParam
  hdf5Path?: string
  matPath?: string
}

export interface AlgorithmNodePostData extends AlgorithmNodeData {
  path: string
  param: ParamMap
  dataFilterParam?: TDataFilterParam
  draftDataFilterParam?: TDataFilterParam
}

export async function runFilterApi(
  workspaceId: number,
  uid: number | string,
  node_id: string,
  data?: TDataFilterParam,
): Promise<string> {
  const response = await axios.post(
    `${BASE_URL}/run/filter/${workspaceId}/${uid}/${node_id}`,
    data,
  )
  return response.data
}

export async function runApi(
  workspaceId: number,
  data: RunPostData,
): Promise<string> {
  const response = await axios.post(`${BASE_URL}/run/${workspaceId}`, data)
  return response.data
}

export async function runByUidApi(
  workspaceId: number,
  uid: string,
  data: Omit<RunPostData, "name">,
): Promise<string> {
  const response = await axios.post(
    `${BASE_URL}/run/${workspaceId}/${uid}`,
    data,
  )
  return response.data
}

export type RunResultDTO = {
  [nodeId: string]: {
    status: string
    message: string
    name: string
    outputPaths?: OutputPathsDTO
  }
}

// Post-run completion status values from backend
export const COMPLETE_STATUS = {
  PROCESSING: "processing",
  SUCCESS: "success",
  ERROR: "error",
} as const

export type CompleteStatus =
  | (typeof COMPLETE_STATUS)[keyof typeof COMPLETE_STATUS]
  | null

// Response wrapper from poll endpoint - includes post-run completion tracking
export type PollRunResultDTO = {
  nodeResults: RunResultDTO
  completeStatus?: CompleteStatus
}

export type OutputPathsDTO = {
  [outputKey: string]: {
    path: string
    type: string
    data_shape: number[]
    max_index: number
  }
}

export async function runResult(data: {
  workspaceId: number
  uid: string
  pendingNodeIdList: string[]
}): Promise<PollRunResultDTO> {
  const { workspaceId, uid, pendingNodeIdList } = data
  const response = await axios.post(
    `${BASE_URL}/run/result/${workspaceId}/${uid}`,
    {
      pendingNodeIdList,
    },
  )
  return response.data
}

export async function cancelResultApi(data: {
  workspaceId: number
  uid: string
}): Promise<RunResultDTO> {
  const { workspaceId, uid } = data
  const response = await axios.post(
    `${BASE_URL}/run/cancel/${workspaceId}/${uid}`,
  )
  return response.data
}
